import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface UseIdleTimeoutProps {
  timeout?: number; // in milliseconds, default 15 minutes
  onIdle?: () => void;
}

export const useIdleTimeout = ({
  timeout = 15 * 60 * 1000, // 15 minutes default
  onIdle
}: UseIdleTimeoutProps = {}) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      toast.error('You have been logged out due to inactivity');
      navigate('/login');
    } catch {
      // Deliberately swallowed. The session cookie is httpOnly and AuthContext keeps no
      // local user cache, so a failed logout request leaves nothing on the client to clean
      // up and nothing useful to tell the user.
    }
  }, [logout, navigate]);

  const handleIdle = useCallback(() => {
    if (onIdle) {
      onIdle();
    } else {
      handleLogout();
    }
  }, [onIdle, handleLogout]);

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      handleIdle();
    }, timeout);
  }, [timeout, handleIdle]);

  useEffect(() => {
    // No authenticated user (anonymous visitor, or session still loading):
    // don't track activity and don't start the idle timer.
    if (!user) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      return;
    }

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any user activity
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [resetTimer, user]);

  return { resetTimer };
};